import React from 'react';
import { css } from '@emotion/react';
import { ApolloError } from '@apollo/client';

import {
  InteractionDetailsInput,
  InteractionType,
  ReactionForInteractionInput,
  useCreateInteractionReactionMutation,
} from '$/GraphQL/schema/schema';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { PlayerMode } from '$/components/WebinarPlayer/types';

import ApolloForm from '../../ApolloForm';
import { useSocketContext } from '../../Socket/SocketContext';
import { OverlayInteractionMode } from '../../Interactions/types';
import { InteractionInfo } from '../../Interactions/InteractionInfo';
import { useAttendeePropertiesContext } from '../../Property/PropertyProvider';

export const OverlayInteractionsParent: React.FC<{
  interactionInEdit?: InteractionInfo;
  items: InteractionInfo[];
  className?: string;
}> = withWebinarPlayerContext(
  [
    'attendee',
    'mode',
    'ewebinar',
    'rooms',
    'playbackPositionRef',
    'playbackPosition',
    'shortIdRef',
  ],
  'OverlayInteractionsParent'
)(({ interactionInEdit, context, className, items }) => {
  const [createReaction] = useCreateInteractionReactionMutation();
  const { connectionId } = useSocketContext();
  const { attendee, mode, ewebinar, rooms, playbackPositionRef, playbackPosition, shortIdRef } =
    context;

  const { updateProperties } = useAttendeePropertiesContext();

  if (!items.length || interactionInEdit) {
    return null;
  }

  return (
    <>
      {items.map((interaction, index) => {
        const OverlayComponent = interaction.renderOverlayComponent();

        const displayState = interaction.cardDisplayState(playbackPosition, mode);

        const showOverAllControls =
          interaction.type === InteractionType.InVideoRegistration && !interaction.reaction;

        return (
          <div
            id='OverlayParent'
            key={interaction.id}
            className={`${className} ${showOverAllControls ? 'z-player-control-bar' : ''}`}
            css={css`
              display: flex;
              visibility: visible;
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              pointer-events: none !important;
              display: flex;
            `}
          >
            <ApolloForm
              key={interaction.id}
              className='w-full h-full'
              css={css`
                > * {
                  pointer-events: auto;
                }
              `}
              onSubmit={async (data: {
                stringAnswer?: string;
                numberAnswer?: string | number;
                arrayAnswer?: string[];
                details?: InteractionDetailsInput;
              }): Promise<void> => {
                if (mode === PlayerMode.edit) {
                  return;
                }

                const detailsKey = interaction.detailsKey();

                const reaction: ReactionForInteractionInput = {
                  interactionId: interaction.id,
                  ewebinarId: ewebinar.id,
                  interactionType: interaction.type,
                  reactionAppearRoom: rooms.getRoomAt(playbackPositionRef.current ?? 0).type,
                  reactedAt: playbackPositionRef.current ?? 0,
                  attendeeId: `${attendee?.id}`,
                  numberAnswer:
                    typeof data.numberAnswer === 'string'
                      ? Number(data.numberAnswer)
                      : data.numberAnswer,
                  stringAnswer: data.stringAnswer,
                  arrayAnswer: data.arrayAnswer,
                  detailsFields: detailsKey ? { [detailsKey]: data.details } : {},
                };

                try {
                  const res = await createReaction({
                    variables: {
                      attendeeId: attendee!.id,
                      data: reaction,
                      connectionId: connectionId ?? shortIdRef,
                    },
                  });
                  if (res.data?.createInteractionReaction?.reaction?.properties) {
                    updateProperties(res.data?.createInteractionReaction.reaction.properties);
                  }
                } catch (e) {
                  // TODO: Show error in card
                  const error = e as ApolloError;
                  console.error('Error creating reaction ', { error });
                }
              }}
            >
              <OverlayComponent
                interaction={items[index]}
                overlayMode={OverlayInteractionMode.Live}
                displayState={displayState}
              />
            </ApolloForm>
          </div>
        );
      })}
    </>
  );
});
